import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  User as FirebaseUser,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './config';
import type { User } from '@/types';

// Create user document in Firestore (privacy-first: user can only access their own data)
export async function createUserDocument(firebaseUser: FirebaseUser, displayName?: string) {
  const userRef = doc(db, 'users', firebaseUser.uid);

  // Check if user already exists
  const userSnap = await getDoc(userRef);
  if (userSnap.exists()) {
    return userSnap.data() as User;
  }

  // Create new user document
  const userData: Omit<User, 'createdAt' | 'updatedAt' | 'lastLoginAt'> & {
    createdAt: ReturnType<typeof serverTimestamp>;
    updatedAt: ReturnType<typeof serverTimestamp>;
    lastLoginAt: ReturnType<typeof serverTimestamp>;
  } = {
    id: firebaseUser.uid,
    email: firebaseUser.email || '',
    displayName: displayName || firebaseUser.displayName || null,
    photoURL: firebaseUser.photoURL || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastLoginAt: serverTimestamp(),
    plan: 'free',
    storageUsedBytes: 0,
    storyCreditsRemaining: 50,
    emailPreferences: {
      dailyStoryEmail: true,
      emailFrequency: 'daily',
      preferredTime: '19:00',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      marketingEmails: true,
    },
    settings: {
      defaultChildId: null,
      theme: 'system',
    },
  };

  await setDoc(userRef, userData);
  return userData as unknown as User;
}

// Sign up with email and password
export async function signUp(email: string, password: string, displayName: string) {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);

  // Update profile with display name
  await updateProfile(userCredential.user, { displayName });

  // Create user document
  await createUserDocument(userCredential.user, displayName);

  return userCredential.user;
}

// Sign in with email and password
export async function signIn(email: string, password: string) {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);

  // Update last login
  const userRef = doc(db, 'users', userCredential.user.uid);
  await setDoc(userRef, { lastLoginAt: serverTimestamp() }, { merge: true });

  return userCredential.user;
}

// Sign in with Google
export async function signInWithGoogle() {
  const provider = new GoogleAuthProvider();
  const userCredential = await signInWithPopup(auth, provider);

  // Create or update user document
  await createUserDocument(userCredential.user);

  return userCredential.user;
}

// Sign out
export async function signOut() {
  await firebaseSignOut(auth);
}

// Reset password
export async function resetPassword(email: string) {
  await sendPasswordResetEmail(auth, email);
}

// Get current user document
export async function getCurrentUserDocument(): Promise<User | null> {
  const currentUser = auth.currentUser;
  if (!currentUser) return null;

  const userRef = doc(db, 'users', currentUser.uid);
  const userSnap = await getDoc(userRef);

  if (!userSnap.exists()) return null;
  return userSnap.data() as User;
}
